import { useRegisterSW } from "virtual:pwa-register/react";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";

const SW_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * 정책
 * - 새 버전 감지 시 항상 배너 노출
 * - 앱 시작 직후 별도 fetch / 즉시 update() 호출은 하지 않음
 * - 포그라운드 복귀 / 주기 체크에서만 registration.update() 수행
 *
 * Mount this once near the root of the app (e.g. inside <App />).
 */
export function AppUpdateBanner() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_, registration) {
      registrationRef.current = registration ?? null;
    },
    onRegisterError(error) {
      Sentry.captureException(error, {
        tags: { source: "service-worker" },
      });
    },
  });

  useEffect(function schedulePeriodicUpdateCheck() {
    const interval = window.setInterval(() => {
      void registrationRef.current?.update();
    }, SW_UPDATE_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  useEffect(function checkUpdateOnForeground() {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void registrationRef.current?.update();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(
    function handleUpdateDetected() {
      if (!needRefresh) return;

      setIsLeaving(false);
      setIsVisible(true);
      // 노출 대비 업데이트 클릭률 = 구버전에 머무는 사용자 비율의 선행지표.
      EventLogger.view(CLIENT_EVENT.APP_UPDATE_BANNER_VIEW);
    },
    [needRefresh],
  );

  function handleDismiss() {
    EventLogger.click(CLIENT_EVENT.APP_UPDATE_DISMISS);
    setNeedRefresh(false);
    setIsLeaving(true);
  }

  function handleUpdate() {
    EventLogger.click(CLIENT_EVENT.APP_UPDATE_CLICK);
    void updateServiceWorker(true);
  }

  function handleAnimationEnd() {
    if (isLeaving) {
      setIsVisible(false);
      setIsLeaving(false);
    }
  }

  if (!isVisible) {
    return null;
  }

  const animation = isLeaving
    ? "slideDown 0.25s cubic-bezier(0.4, 0, 1, 1) both"
    : "slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both";

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
        aria-live="polite"
        aria-label="App update notification"
        className="fixed bottom-10 left-1/2 z-[9999] w-[calc(100%-48px)] max-w-sm -translate-x-1/2"
        style={{ animation }}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className="flex items-center gap-3 rounded-2xl bg-[#1a1a1a]/95 px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.32)] backdrop-blur-md">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
            <UpdateIcon />
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-subtitle6 text-white/90">New version is available</span>
            <span className="text-body4 text-white/50">Update to the latest features</span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-lg px-2 py-1.5 text-[12px] font-medium text-white/40 transition-colors active:text-white/60"
            >
              Later
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              className="rounded-lg bg-blue-500 px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity active:opacity-80"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function UpdateIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-blue-500"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

const KEYFRAMES = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  @keyframes slideDown {
    from { opacity: 1; transform: translateX(-50%) translateY(0); }
    to   { opacity: 0; transform: translateX(-50%) translateY(12px); }
  }
`;
